/**
 * A caixa "Nova Subcategoria" de Cadastros.
 *
 * Papel: reproduz `#modal-sub` e `saveSubModal` (l. 1283–1312, 5923–5980): a
 * categoria somente leitura, o campo das subcategorias com "+ Adicionar" e
 * Enter, as fichas, a dica, e "Salvar Subcategorias".
 *
 * Como usar: quem monta passa a categoria e desmonta a caixa em `onFechar`.
 * Acrescentar uma que já existe na categoria ou nas fichas avisa "Subcategoria
 * já existe"; salvar aproveita o texto ainda no campo, recusa a lista vazia e a
 * lista só de repetidas, e grava só as que faltam.
 */
import { type Categoria } from '@concierge/shared';
import { useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { useCriarSubcategorias } from '../hooks/usar-financas';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { CLASSE_DE_SOMENTE_LEITURA, CLASSE_DO_CONTROLE, Campo } from './kit/campo';
import { Ficha } from './kit/ficha';

interface ModalNovaSubcategoriaProps {
  categoria: Categoria;
  onFechar: () => void;
}

/**
 * A caixa "Nova Subcategoria".
 *
 * @param categoria - A categoria que recebe as subcategorias.
 * @param onFechar - Desmonta a caixa.
 * @returns A caixa de 460 px.
 */
export function ModalNovaSubcategoria({ categoria, onFechar }: ModalNovaSubcategoriaProps) {
  const notificar = useNotificacao();
  const criar = useCriarSubcategorias();
  const campo = useRef<HTMLInputElement>(null);
  const [texto, setTexto] = useState('');
  const [fichas, setFichas] = useState<string[]>([]);

  const existentes = categoria.subcategorias.map((subcategoria) => subcategoria.nome);

  /** Acrescenta o nome digitado às fichas, ou avisa que já existe. */
  function handleAdicionar() {
    const aparado = texto.trim();
    if (aparado === '') {
      campo.current?.focus();
      return;
    }
    if (fichas.includes(aparado) || existentes.includes(aparado)) {
      notificar('Subcategoria já existe', false);
      campo.current?.select();
      return;
    }
    setFichas((anteriores) => [...anteriores, aparado]);
    setTexto('');
    campo.current?.focus();
  }

  /** Grava as que faltam, aproveitando o texto ainda no campo. */
  async function handleSalvar() {
    const aparado = texto.trim();
    const todas =
      aparado !== '' && !fichas.includes(aparado) && !existentes.includes(aparado)
        ? [...fichas, aparado]
        : fichas;
    if (todas.length === 0) {
      notificar('Adicione pelo menos uma subcategoria', false);
      return;
    }
    const novas = todas.filter((nome) => !existentes.includes(nome));
    if (novas.length === 0) {
      notificar('Todas as subcategorias já existem', false);
      return;
    }
    try {
      const { adicionadas } = await criar.mutateAsync({
        categoriaId: categoria.id,
        nomes: novas,
      });
      onFechar();
      const plural = adicionadas > 1 ? 's' : '';
      notificar(
        `✓ ${String(adicionadas)} subcategoria${plural} adicionada${plural} em "${categoria.nome}"`,
      );
    } catch (erro) {
      notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Nova Subcategoria"
      largura={460}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleSalvar()} disabled={criar.isPending}>
            Salvar Subcategorias
          </Botao>
        </>
      }
    >
      <Campo rotulo="Categoria" id="subcategoria-categoria">
        <input
          id="subcategoria-categoria"
          readOnly
          value={categoria.nome}
          className={cn(CLASSE_DO_CONTROLE, CLASSE_DE_SOMENTE_LEITURA)}
        />
      </Campo>
      <Campo rotulo="Subcategorias" id="subcategoria-nome">
        <div className="flex gap-1.5">
          <input
            ref={campo}
            id="subcategoria-nome"
            // A origem põe o foco no campo ao abrir (l. 5937).
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Nome da subcategoria..."
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') {
                evento.preventDefault();
                handleAdicionar();
              }
            }}
            // A borda continua bege no foco: a origem a escreve no `style` do
            // campo, que vence o `:focus` de `.field`; só o anel aparece.
            className={cn(CLASSE_DO_CONTROLE, 'flex-1 py-[7px] focus:border-emb-borda')}
          />
          <Botao variante="fantasma" tamanho="sm" onClick={handleAdicionar}>
            + Adicionar
          </Botao>
        </div>
        <div className="mt-2 flex min-h-6 flex-wrap gap-1.5">
          {fichas.map((ficha) => (
            <Ficha
              key={ficha}
              texto={ficha}
              onRemover={() =>
                setFichas((anteriores) => anteriores.filter((item) => item !== ficha))
              }
            />
          ))}
        </div>
        <div className="mt-2 text-[11px] text-texto-fraco">
          Pressione Enter ou clique &quot;+ Adicionar&quot; para incluir cada subcategoria.
          Salve ao final.
        </div>
      </Campo>
    </CaixaDeDialogo>
  );
}
